import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Backdrop,
  CircularProgress
} from '@mui/material';
import MainCard from '../../components/MainCard';
import Sidebar from "../../menu-items/Sidebar";
import axios from 'axios';
import { Autocomplete } from '@mui/material';
import Cookies from 'js-cookie';

const Waitlist = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [mode, setMode] = useState('patient'); // ✅ fixed here
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [consentBundles, setConsentBundles] = useState([]);
  const [selectedConsentBundle, setSelectedConsentBundle] = useState('');

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  const [practices, setPractices] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState('');

  const [totalCount, setTotalCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const adminToken = Cookies.get('admintoken');
    console.log('Admin token from cookie:', adminToken);
  }, []);

  // ------- dropdowns -------
  useEffect(() => {
    const fetchConsentBundles = async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/consent_bundles`;

        const config = {
          headers: {
            Authorization: `${token}`,
            'Content-Type': 'application/json',
          },
        };

        const response = await axios.get(url, config);
        setConsentBundles(response.data ?? []);
      } catch (error) {
        console.error('Error fetching consent bundles:', error);
      }
    };
    fetchConsentBundles();
  }, []);

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/practices`;

        const config = { headers: { Authorization: `${token}` } };
        const response = await axios.get(url, config);
        setPractices(response.data ?? []);
      } catch (error) {
        console.error('Error fetching practices:', error);
      }
    };
    fetchPractices();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = axios.defaults.headers.common.Authorization;
        const baseUrl = process.env.REACT_APP_BASE_URL;
        const url = `${baseUrl}admin/dropdown/patients`;

        const config = { headers: { Authorization: `${token}` } };
        const response = await axios.get(url, config);
        setUsers(response.data ?? []);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  // ------- core fetch (server-side pagination) -------
  const fetchData = async () => {
    const token = axios.defaults.headers.common.Authorization;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const params = new URLSearchParams({
      mode,
      limit: rowsPerPage.toString(),
      offset: (page + 1).toString(),
    });

    if (dateFrom) params.append('startDate', dateFrom);
    if (dateTo) params.append('endDate', dateTo);
    if (selectedConsentBundle) params.append('consentBundle', selectedConsentBundle);
    if (selectedUser) params.append('patient', selectedUser);
    if (selectedPractice) params.append('practice', selectedPractice);

    const url = `${baseUrl}admin/reports/tracking_report?${params.toString()}`;
    const config = { headers: { Authorization: `${token}` } };

    try {
      const response = await axios.get(url, config);
      const resData = response.data ?? {};
      setData(resData.results ?? []);
      setTotalCount(resData.total ?? 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    setPage(0);
    fetchData();
  }, [mode, dateFrom, dateTo, selectedConsentBundle, selectedUser, selectedPractice]);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  // ------- CSV: download current page -------
  const downloadPageAsCSV = () => {
    const headers = [
      '#',
      'Consent bundle code',
      'Patient name',
      'Practice',
      'Status',
      'Session start time',
      'Session end time',
      'Duration',
    ];
    const csvRows = [headers.join(',')];

    data.forEach((row, index) => {
      const cells = [
        (page * rowsPerPage + index + 1).toString(),
        row?.consent_bundle_code ?? '',
        row?.patient_name ?? '',
        row?.practice ?? '',
        row?.status ?? '',
        row?.session_start_time ?? '',
        row?.session_end_time ?? '',
        row?.duration ?? '',
      ].map(v =>
        typeof v === 'string' && (v.includes(',') || v.includes('"'))
          ? `"${v.replace(/"/g, '""')}"`
          : v
      );
      csvRows.push(cells.join(','));
    });

    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'Time_Tracking_Page.csv';
    link.click();
  };

  // ------- normalize payload -------
  const normalizePayload = (payload) => {
    const results = Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : Object.values(payload ?? {});
    const total =
      typeof payload?.total === 'number'
        ? payload.total
        : Array.isArray(payload?.results)
        ? payload.results.length
        : Array.isArray(payload?.data)
        ? payload.data.length
        : Array.isArray(payload)
        ? payload.length
        : results.length;
    return { results, total };
  };

  // ------- CSV: download ALL pages -------
  const fetchPageAll = async (pageIndex1Based, limit) => {
    const token = axios.defaults.headers.common.Authorization;
    const baseUrl = process.env.REACT_APP_BASE_URL;

    const params = new URLSearchParams({
      mode,
      limit: String(limit),
      offset: String(pageIndex1Based),
    });

    if (dateFrom) params.append('startDate', dateFrom);
    if (dateTo) params.append('endDate', dateTo);
    if (selectedConsentBundle) params.append('consentBundle', selectedConsentBundle);
    if (selectedUser) params.append('patient', selectedUser);
    if (selectedPractice) params.append('practice', selectedPractice);

    const url = `${baseUrl}admin/reports/tracking_report?${params.toString()}`;
    const res = await axios.get(url, { headers: { Authorization: `${token}` } });
    return normalizePayload(res.data);
  };

  const downloadAllAsCSV = async () => {
    setExporting(true);
    try {
      const pageSize = 500;
      const first = await fetchPageAll(1, pageSize);
      const allRows = [...first.results];
      const total = first.total;
      const totalPages = Math.ceil(total / pageSize);

      for (let p = 2; p <= totalPages; p++) {
        const { results } = await fetchPageAll(p, pageSize);
        allRows.push(...results);
      }

      const headers = [
        '#',
        'Consent bundle code',
        'Patient name',
        'Practice',
        'Status',
        'Session start time',
        'Session end time',
        'Duration',
      ];
      const csvRows = [headers.join(',')];

      allRows.forEach((row, idx) => {
        const cells = [
          (idx + 1).toString(),
          row?.consent_bundle_code ?? '',
          row?.patient_name ?? '',
          row?.practice ?? '',
          row?.status ?? '',
          row?.session_start_time ?? '',
          row?.session_end_time ?? '',
          row?.duration ?? '',
        ].map(v =>
          typeof v === 'string' && (v.includes(',') || v.includes('"'))
            ? `"${v.replace(/"/g, '""')}"`
            : v
        );
        csvRows.push(cells.join(','));
      });

      const csvData = csvRows.join('\n');
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Time_Tracking_${mode}_ALL.csv`;
      link.click();
    } catch (err) {
      console.error('Download ALL CSV failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const displayedData = data;

  return (
    <Box sx={{ border: '1px solid #ccc', padding: 5, height: '100vh' }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
              Time Tracking - Patient
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" color="primary" onClick={downloadPageAsCSV}>
                Download Page CSV
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={downloadAllAsCSV}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={18} /> : null}
              >
                {exporting ? 'Preparing CSV…' : 'Download ALL CSV'}
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              options={consentBundles}
              getOptionLabel={(option) => option.label || ''}
              value={consentBundles.find((bundle) => bundle.value === selectedConsentBundle) || null}
              onChange={(_e, newValue) => setSelectedConsentBundle(newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Consent Bundle" size="small" sx={{ minWidth: 250 }} />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />

            <Autocomplete
              options={[{ label: 'All', value: '' }, ...users]}
              getOptionLabel={(option) => option.label || ''}
              value={users.find((user) => user.value === selectedUser) || { label: 'All', value: '' }}
              onChange={(_e, newValue) => setSelectedUser(newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Patients" size="small" sx={{ minWidth: 250 }} />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />

            <Autocomplete
              options={[{ label: 'All', value: '' }, ...practices]}
              getOptionLabel={(option) => option.label || ''}
              value={practices.find((practice) => practice.value === selectedPractice) || { label: 'All', value: '' }}
              onChange={(_e, newValue) => setSelectedPractice(newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField {...params} label="Practice" size="small" sx={{ minWidth: 250 }} />
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />

            <TextField
              label="Date From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Date To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Button variant="contained" onClick={fetchData} size="small">
              Apply Filters
            </Button>

            {(dateFrom || dateTo || selectedConsentBundle || selectedUser || selectedPractice) && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setSelectedConsentBundle('');
                  setSelectedUser('');
                  setSelectedPractice('');
                  setPage(0);
                  fetchData();
                }}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <MainCard title="">
            <TablePagination
              rowsPerPageOptions={[50, 100, 200, 1000]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '15px',
                boxShadow: 3,
                overflow: 'hidden',
                marginTop: 2,
              }}
            >
              <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2', color: '#fff' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Consent bundle code</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Patient name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Practice</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Session start time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Session end time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5', cursor: 'pointer' },
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>{row?.consent_bundle_code}</TableCell>
                        <TableCell>{row?.patient_name}</TableCell>
                        <TableCell>{row?.practice}</TableCell>
                        <TableCell>{row?.status}</TableCell>
                        <TableCell>{row?.session_start_time}</TableCell>
                        <TableCell>{row?.session_end_time}</TableCell>
                        <TableCell>{row?.duration}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[50, 100, 200, 1000]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={exporting}>
        <CircularProgress />
      </Backdrop>
    </Box>
  );
};

export default Waitlist;
